import { FeatureIcons } from "~/components/shared/subscription-comparison/images/featureIcons";

export const title = "Velg riktig TV-pakke for deg";
type Features =
  | "NrOfChannels"
  | "NrOfRooms"
  | "TV2Basis"
  | "HBOMaxBasis"
  | "ViaplayFilmSerierWithAds"
  | "SkyShowtime"
  | "NordiskFilm"
  | "FilmFavoritter"
  | "TV2Sport"
  | "VSport"
  | "Eurosport"
  | "ViaplayTotal"
  | "Shipping";

export enum FeatureIndicatorDisplayType {
  Check = "check",
  StrongText = "strongText",
  StrongTextWithCheck = "strongTextWithCheck",
  WeakText = "weakText",
  Warning = "warning",
}

export type FeatureIndicator =
  | { displayType: FeatureIndicatorDisplayType.Check; text?: never }
  | { displayType: FeatureIndicatorDisplayType.Warning; text?: never }
  | {
      text: string;
      displayType:
        | FeatureIndicatorDisplayType.StrongText
        | FeatureIndicatorDisplayType.WeakText;
    }
  | {
      text: string;
      displayType: FeatureIndicatorDisplayType.StrongTextWithCheck;
    };
export type StaticComparisonData = {
  subscriptionIccProductId: number;
  featureIndicators: Record<Features, FeatureIndicator>;
}[];

export const comparableSubscriptionsStaticData = (
  isViaplayBreachActive: boolean,
): StaticComparisonData => [
  {
    // Rikspakken
    subscriptionIccProductId: 6,
    featureIndicators: {
      NrOfChannels: {
        text: "61",
        displayType: FeatureIndicatorDisplayType.StrongText,
      },
      NrOfRooms: {
        text: "4",
        displayType: FeatureIndicatorDisplayType.StrongText,
      },
      TV2Basis: {
        displayType: FeatureIndicatorDisplayType.Check,
      },
      HBOMaxBasis: { displayType: FeatureIndicatorDisplayType.Check },
      ViaplayFilmSerierWithAds: {
        displayType: FeatureIndicatorDisplayType.Check,
      },
      SkyShowtime: { displayType: FeatureIndicatorDisplayType.Check },
      NordiskFilm: { displayType: FeatureIndicatorDisplayType.Check },
      FilmFavoritter: isViaplayBreachActive
        ? { displayType: FeatureIndicatorDisplayType.Warning }
        : { displayType: FeatureIndicatorDisplayType.Check },
      TV2Sport: { displayType: FeatureIndicatorDisplayType.Check },
      VSport: isViaplayBreachActive
        ? { displayType: FeatureIndicatorDisplayType.Warning }
        : { displayType: FeatureIndicatorDisplayType.Check },
      Eurosport: { displayType: FeatureIndicatorDisplayType.Check },
      Shipping: { displayType: FeatureIndicatorDisplayType.Check },
      ViaplayTotal: isViaplayBreachActive
        ? { displayType: FeatureIndicatorDisplayType.Warning }
        : {
            displayType: FeatureIndicatorDisplayType.StrongText,
            text: "Tilleggspakke",
          },
    },
  },
  {
    // SportsPakken
    subscriptionIccProductId: 299,
    featureIndicators: {
      NrOfChannels: {
        text: "58",
        displayType: FeatureIndicatorDisplayType.StrongText,
      },
      NrOfRooms: {
        text: "4",
        displayType: FeatureIndicatorDisplayType.StrongText,
      },
      TV2Basis: { displayType: FeatureIndicatorDisplayType.Check },
      HBOMaxBasis: {
        text: "Tilleggspakke",
        displayType: FeatureIndicatorDisplayType.StrongText,
      },
      ViaplayFilmSerierWithAds: {
        text: "Tilleggspakke",
        displayType: FeatureIndicatorDisplayType.StrongText,
      },
      SkyShowtime: {
        text: "Ikke tilgjengelig",
        displayType: FeatureIndicatorDisplayType.WeakText,
      },
      NordiskFilm: {
        text: "Ikke tilgjengelig",
        displayType: FeatureIndicatorDisplayType.WeakText,
      },
      FilmFavoritter: {
        text: "Ikke tilgjengelig",
        displayType: FeatureIndicatorDisplayType.WeakText,
      },
      TV2Sport: { displayType: FeatureIndicatorDisplayType.Check },
      VSport: isViaplayBreachActive
        ? { displayType: FeatureIndicatorDisplayType.Warning }
        : { displayType: FeatureIndicatorDisplayType.Check },
      Eurosport: { displayType: FeatureIndicatorDisplayType.Check },
      Shipping: { displayType: FeatureIndicatorDisplayType.Check },
      ViaplayTotal: isViaplayBreachActive
        ? { displayType: FeatureIndicatorDisplayType.Warning }
        : {
            displayType: FeatureIndicatorDisplayType.StrongText,
            text: "Tilleggspakke",
          },
    },
  },
  {
    // FleksiPakken
    subscriptionIccProductId: 316,
    featureIndicators: {
      NrOfChannels: {
        text: "16+6",
        displayType: FeatureIndicatorDisplayType.StrongText,
      },
      NrOfRooms: {
        text: "1",
        displayType: FeatureIndicatorDisplayType.StrongText,
      },
      TV2Basis: { displayType: FeatureIndicatorDisplayType.Check },
      HBOMaxBasis: {
        text: "Valgfri",
        displayType: FeatureIndicatorDisplayType.StrongTextWithCheck,
      },
      ViaplayFilmSerierWithAds: {
        text: "Valgfri",
        displayType: FeatureIndicatorDisplayType.StrongTextWithCheck,
      },
      SkyShowtime: {
        text: "Ikke tilgjengelig",
        displayType: FeatureIndicatorDisplayType.WeakText,
      },
      NordiskFilm: {
        text: "Ikke tilgjengelig",
        displayType: FeatureIndicatorDisplayType.WeakText,
      },
      FilmFavoritter: {
        text: "Ikke tilgjengelig",
        displayType: FeatureIndicatorDisplayType.WeakText,
      },
      TV2Sport: {
        text: "Tilleggspakke",
        displayType: FeatureIndicatorDisplayType.StrongText,
      },
      VSport: {
        text: "Ikke tilgjengelig",
        displayType: FeatureIndicatorDisplayType.WeakText,
      },
      Eurosport: {
        text: "Tilleggspakke",
        displayType: FeatureIndicatorDisplayType.StrongText,
      },
      Shipping: { displayType: FeatureIndicatorDisplayType.Check },
      ViaplayTotal: isViaplayBreachActive
        ? { displayType: FeatureIndicatorDisplayType.Warning }
        : {
            displayType: FeatureIndicatorDisplayType.StrongText,
            text: "Tilleggspakke",
          },
    },
  },
  {
    // StrommePakken
    subscriptionIccProductId: 315,
    featureIndicators: {
      NrOfChannels: {
        text: "50",
        displayType: FeatureIndicatorDisplayType.StrongText,
      },
      NrOfRooms: {
        text: "4",
        displayType: FeatureIndicatorDisplayType.StrongText,
      },
      TV2Basis: { displayType: FeatureIndicatorDisplayType.Check },
      HBOMaxBasis: { displayType: FeatureIndicatorDisplayType.Check },
      ViaplayFilmSerierWithAds: {
        displayType: FeatureIndicatorDisplayType.Check,
      },
      SkyShowtime: { displayType: FeatureIndicatorDisplayType.Check },
      NordiskFilm: { displayType: FeatureIndicatorDisplayType.Check },
      FilmFavoritter: {
        text: "Ikke tilgjengelig",
        displayType: FeatureIndicatorDisplayType.WeakText,
      },
      TV2Sport: {
        text: "Tilleggspakke",
        displayType: FeatureIndicatorDisplayType.StrongText,
      },
      VSport: {
        text: "Ikke tilgjengelig",
        displayType: FeatureIndicatorDisplayType.WeakText,
      },
      Eurosport: {
        text: "Tilleggspakke",
        displayType: FeatureIndicatorDisplayType.StrongText,
      },
      Shipping: { displayType: FeatureIndicatorDisplayType.Check },
      ViaplayTotal: isViaplayBreachActive
        ? { displayType: FeatureIndicatorDisplayType.Warning }
        : {
            displayType: FeatureIndicatorDisplayType.StrongText,
            text: "Tilleggspakke",
          },
    },
  },
];

export type ComparisonCategory = {
  name: string;
  icon: keyof typeof FeatureIcons;
  type: Features;
};

export const comparisonCategories: ComparisonCategory[] = [
  {
    name: "Antall kanaler",
    icon: "Channels",
    type: "NrOfChannels",
  },
  {
    name: "TV 2 Play Start**",
    icon: "TV2Sport",
    type: "TV2Basis",
  },
  {
    name: "HBO Max Basis**",
    icon: "HBOMax",
    type: "HBOMaxBasis",
  },
  {
    name: "Viaplay Film & Serier**",
    icon: "Viaplay",
    type: "ViaplayFilmSerierWithAds",
  },
  {
    name: "SkyShowtime",
    icon: "SkyShowtime",
    type: "SkyShowtime",
  },
  {
    name: "Nordisk Film+",
    icon: "NordiskFilmPlus",
    type: "NordiskFilm",
  },
  {
    name: "TV 2 Sport-kanalene",
    icon: "TV2Sport",
    type: "TV2Sport",
  },
  {
    name: "V sport-kanalene",
    icon: "Viasport",
    type: "VSport",
  },
  {
    name: "Eurosport-kanalene",
    icon: "Eurosport",
    type: "Eurosport",
  },
  {
    name: "Gratis hjemlevering",
    icon: "Shipping",
    type: "Shipping",
  },
];
